<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore, useFunctionGetter } from 'dashboard/composables/store';
import daftaraAPI from 'dashboard/api/daftara';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const inboxes = useMapGetter('inboxes/getInboxes');
const integration = useFunctionGetter('integrations/getIntegration', 'daftara');

const logoUrl = '/dashboard/images/integrations/daftara.png';
const isLoading = ref(true);
const isWorking = ref(false);
const activeTab = ref('clients');
const status = ref({});
const defaultInboxId = ref('');
const connectionForm = reactive({ subdomain: '', apiKey: '', bearerToken: '', webhookSecret: '' });
const creditUsageForm = reactive({ client_id: '', package_id: '', amount: 1, description: '' });

const resources = reactive({
  clients: { page: 1, payload: null, loading: false },
  invoices: { page: 1, payload: null, loading: false },
  products: { page: 1, payload: null, loading: false },
  productCategories: { page: 1, payload: null, loading: false },
  invoicePayments: { page: 1, payload: null, loading: false },
  clientPayments: { page: 1, payload: null, loading: false },
  appointments: { page: 1, payload: null, loading: false },
  bookings: { page: 1, payload: null, loading: false },
  packages: { page: 1, payload: null, loading: false },
  subscriptions: { page: 1, payload: null, loading: false },
  creditUsages: { page: 1, payload: null, loading: false },
  followUps: { page: 1, payload: null, loading: false },
});

const tabs = computed(() => [
  { key: 'clients', label: t('INTEGRATION_SETTINGS.DAFTARA.TABS.CLIENTS') },
  { key: 'invoices', label: t('INTEGRATION_SETTINGS.DAFTARA.TABS.INVOICES') },
  { key: 'products', label: t('INTEGRATION_SETTINGS.DAFTARA.TABS.PRODUCTS') },
  { key: 'subscriptions', label: t('INTEGRATION_SETTINGS.DAFTARA.TABS.SUBSCRIPTIONS') },
  { key: 'packages', label: t('INTEGRATION_SETTINGS.DAFTARA.TABS.PACKAGES') },
  { key: 'appointments', label: t('INTEGRATION_SETTINGS.DAFTARA.TABS.APPOINTMENTS') },
  { key: 'bookings', label: t('INTEGRATION_SETTINGS.DAFTARA.TABS.BOOKINGS') },
  { key: 'invoicePayments', label: t('INTEGRATION_SETTINGS.DAFTARA.TABS.INVOICE_PAYMENTS') },
  { key: 'clientPayments', label: t('INTEGRATION_SETTINGS.DAFTARA.TABS.CLIENT_PAYMENTS') },
  { key: 'creditUsages', label: t('INTEGRATION_SETTINGS.DAFTARA.TABS.CREDIT_USAGE') },
  { key: 'followUps', label: t('INTEGRATION_SETTINGS.DAFTARA.TABS.FOLLOW_UPS') },
  { key: 'webhooks', label: t('INTEGRATION_SETTINGS.DAFTARA.TABS.WEBHOOKS') },
]);

const connected = computed(() => !!status.value.connected);
const configured = computed(() => !!status.value.configured);
const currentResource = computed(() => resources[activeTab.value]);
const currentItems = computed(() => extractItems(currentResource.value?.payload));
const inboxOptions = computed(() => inboxes.value || []);
const recentWebhooks = computed(() => status.value.recent_webhooks || []);
const siteName = computed(() => {
  const site = status.value.site || {};
  return site.business_name || site.subdomain || [site.first_name, site.last_name].filter(Boolean).join(' ') || status.value.subdomain || '';
});

const extractItems = payload => {
  if (!payload) return [];
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.data?.data)) return payload.data.data;
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.result)) return payload.result;
  return [];
};

const unwrapRecord = record => {
  const wrapper = [
    'Client',
    'Invoice',
    'Product',
    'ProductCategory',
    'InvoicePayment',
    'ClientPayment',
    'InvoiceAppointment',
    'Booking',
    'CreditCharge',
    'CreditUsage',
    'Package',
  ].find(key => record?.[key]);
  return wrapper ? record[wrapper] : record;
};

const rowTitle = record => {
  const item = unwrapRecord(record);
  return item.business_name || item.name || item.full_name || item.title || item.number || item.id || '-';
};

const rowDetail = record => {
  const item = unwrapRecord(record);
  return item.email || item.phone || item.phone1 || item.mobile || item.total || item.amount || item.price || item.expiry_date || item.start_date || '-';
};

const rowStatus = record => {
  const item = unwrapRecord(record);
  return item.status || item.active || item.type || item.created || item.modified || '-';
};

const canCreateConversation = computed(() =>
  ['invoices', 'subscriptions', 'appointments', 'bookings', 'invoicePayments', 'clientPayments'].includes(activeTab.value)
);

const loadStatus = async () => {
  const { data } = await daftaraAPI.status();
  status.value = data;
  defaultInboxId.value = data.default_inbox_id || '';
  connectionForm.subdomain = data.subdomain || connectionForm.subdomain;
};

const redirectIfServiceDisabled = () => {
  if (status.value.tenant_enabled !== false) return false;

  router.replace({ path: route.path.replace(/\/[^/]+\/?$/, '') });
  return true;
};

const requestForTab = tab => {
  const resource = resources[tab];
  const params = { page: resource.page, limit: 25 };
  const requestMap = {
    clients: () => daftaraAPI.clients(params),
    invoices: () => daftaraAPI.invoices(params),
    products: () => daftaraAPI.products(params),
    productCategories: () => daftaraAPI.productCategories(params),
    invoicePayments: () => daftaraAPI.invoicePayments(params),
    clientPayments: () => daftaraAPI.clientPayments(params),
    appointments: () => daftaraAPI.invoiceAppointments(params),
    bookings: () => daftaraAPI.bookings(params),
    packages: () => daftaraAPI.packages(params),
    subscriptions: () => daftaraAPI.creditCharges(params),
    creditUsages: () => daftaraAPI.creditUsages(params),
    followUps: () => daftaraAPI.followUpStatuses({ type: 'client' }),
  };
  return requestMap[tab]?.();
};

const loadResource = async (tab = activeTab.value) => {
  if (!connected.value || tab === 'webhooks') return;
  const resource = resources[tab];
  resource.loading = true;
  try {
    const { data } = await requestForTab(tab);
    resource.payload = data;
  } catch (error) {
    useAlert(error.response?.data?.error || t('INTEGRATION_SETTINGS.DAFTARA.API.ERROR'));
  } finally {
    resource.loading = false;
  }
};

const connect = async () => {
  isWorking.value = true;
  try {
    await daftaraAPI.connect({
      subdomain: connectionForm.subdomain,
      api_key: connectionForm.apiKey,
      bearer_token: connectionForm.bearerToken,
      webhook_secret: connectionForm.webhookSecret,
      default_inbox_id: defaultInboxId.value,
    });
    connectionForm.apiKey = '';
    connectionForm.bearerToken = '';
    await loadStatus();
    useAlert(t('INTEGRATION_SETTINGS.DAFTARA.API.CONNECTED'));
  } catch (error) {
    useAlert(error.response?.data?.error || t('INTEGRATION_SETTINGS.DAFTARA.API.ERROR'));
  } finally {
    isWorking.value = false;
  }
};

const saveSettings = async () => {
  isWorking.value = true;
  try {
    await daftaraAPI.updateSettings({
      default_inbox_id: defaultInboxId.value,
      webhook_secret: connectionForm.webhookSecret,
      bearer_token: connectionForm.bearerToken,
    });
    connectionForm.bearerToken = '';
    await loadStatus();
    useAlert(t('INTEGRATION_SETTINGS.DAFTARA.API.SAVED'));
  } catch (error) {
    useAlert(error.response?.data?.error || t('INTEGRATION_SETTINGS.DAFTARA.API.ERROR'));
  } finally {
    isWorking.value = false;
  }
};

const disconnect = async () => {
  if (!window.confirm(t('INTEGRATION_SETTINGS.DAFTARA.DISCONNECT_CONFIRM'))) return;
  isWorking.value = true;
  try {
    await daftaraAPI.disconnect();
    await loadStatus();
    useAlert(t('INTEGRATION_SETTINGS.DAFTARA.API.DISCONNECTED'));
  } catch (error) {
    useAlert(error.response?.data?.error || t('INTEGRATION_SETTINGS.DAFTARA.API.ERROR'));
  } finally {
    isWorking.value = false;
  }
};

const importClient = async record => {
  isWorking.value = true;
  try {
    await daftaraAPI.importClient(unwrapRecord(record));
    useAlert(t('INTEGRATION_SETTINGS.DAFTARA.API.CLIENT_IMPORTED'));
  } catch (error) {
    useAlert(error.response?.data?.error || t('INTEGRATION_SETTINGS.DAFTARA.API.ERROR'));
  } finally {
    isWorking.value = false;
  }
};

const createConversation = async record => {
  if (!defaultInboxId.value) {
    useAlert(t('INTEGRATION_SETTINGS.DAFTARA.SELECT_INBOX_FIRST'));
    return;
  }
  isWorking.value = true;
  try {
    await daftaraAPI.createConversation({
      resource_type: activeTab.value,
      resource: record,
      inbox_id: defaultInboxId.value,
    });
    useAlert(t('INTEGRATION_SETTINGS.DAFTARA.API.CONVERSATION_CREATED'));
  } catch (error) {
    useAlert(error.response?.data?.error || t('INTEGRATION_SETTINGS.DAFTARA.API.ERROR'));
  } finally {
    isWorking.value = false;
  }
};

const addCreditUsage = async () => {
  isWorking.value = true;
  try {
    await daftaraAPI.addCreditUsage(creditUsageForm);
    await loadResource('creditUsages');
    useAlert(t('INTEGRATION_SETTINGS.DAFTARA.API.CREDIT_USAGE_ADDED'));
  } catch (error) {
    useAlert(error.response?.data?.error || t('INTEGRATION_SETTINGS.DAFTARA.API.ERROR'));
  } finally {
    isWorking.value = false;
  }
};

const setPage = async direction => {
  const resource = currentResource.value;
  const nextPage = resource.page + direction;
  if (nextPage < 1) return;
  resource.page = nextPage;
  await loadResource(activeTab.value);
};

const switchTab = async tab => {
  activeTab.value = tab;
  await loadResource(tab);
};

const initialize = async () => {
  try {
    await Promise.all([store.dispatch('integrations/get'), store.dispatch('inboxes/get')]);
    await loadStatus();
    if (redirectIfServiceDisabled()) return;
    if (connected.value) await loadResource(activeTab.value);
  } finally {
    isLoading.value = false;
  }
};

onMounted(initialize);
</script>

<template>
  <SettingsLayout :is-loading="isLoading">
    <template #header>
      <BaseSettingsHeader
        :title="$t('INTEGRATION_SETTINGS.DAFTARA.HEADER')"
        :description="$t('INTEGRATION_SETTINGS.DAFTARA.DESCRIPTION')"
        :back-button-label="$t('INTEGRATION_SETTINGS.HEADER')"
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-5">
        <section class="flex flex-col gap-4 rounded-lg outline outline-1 outline-n-container bg-n-card p-5 md:flex-row md:items-center md:justify-between">
          <div class="flex gap-4">
            <img :src="logoUrl" class="h-14 w-14 rounded-lg border border-n-weak bg-n-alpha-2" alt="Daftara" />
            <div>
              <h3 class="text-heading-2 text-n-slate-12">{{ integration.name || 'Daftara' }}</h3>
              <p class="mt-1 text-body-main text-n-slate-11">{{ integration.description || $t('INTEGRATION_SETTINGS.DAFTARA.DESCRIPTION') }}</p>
              <p v-if="siteName" class="mt-2 text-sm text-n-teal-11">{{ $t('INTEGRATION_SETTINGS.DAFTARA.CONNECTED_TO', { site: siteName }) }}</p>
              <p v-else-if="!configured" class="mt-2 text-sm text-n-ruby-11">{{ $t('INTEGRATION_SETTINGS.DAFTARA.NOT_CONFIGURED') }}</p>
            </div>
          </div>
          <Button v-if="connected" faded ruby :is-loading="isWorking" :label="$t('INTEGRATION_SETTINGS.DAFTARA.DISCONNECT')" @click="disconnect" />
        </section>

        <section class="rounded-lg outline outline-1 outline-n-container bg-n-card p-4">
          <div class="grid gap-4 md:grid-cols-2">
            <label class="flex flex-col gap-1 text-sm text-n-slate-12">
              <span>{{ $t('INTEGRATION_SETTINGS.DAFTARA.DEFAULT_INBOX') }}</span>
              <select v-model="defaultInboxId" class="h-10 rounded-md border border-n-weak bg-n-solid-2 px-3">
                <option value="">{{ $t('INTEGRATION_SETTINGS.DAFTARA.NO_INBOX') }}</option>
                <option v-for="inbox in inboxOptions" :key="inbox.id" :value="inbox.id">{{ inbox.name }}</option>
              </select>
            </label>
            <label class="flex flex-col gap-1 text-sm text-n-slate-12">
              <span>{{ $t('INTEGRATION_SETTINGS.DAFTARA.SUBDOMAIN') }}</span>
              <Input v-model="connectionForm.subdomain" :disabled="connected" placeholder="example" />
            </label>
            <label class="flex flex-col gap-1 text-sm text-n-slate-12">
              <span>{{ $t('INTEGRATION_SETTINGS.DAFTARA.API_KEY') }}</span>
              <Input v-model="connectionForm.apiKey" type="password" :disabled="connected" />
            </label>
            <label class="flex flex-col gap-1 text-sm text-n-slate-12">
              <span>{{ $t('INTEGRATION_SETTINGS.DAFTARA.BEARER_TOKEN') }}</span>
              <Input v-model="connectionForm.bearerToken" type="password" />
            </label>
            <label class="flex flex-col gap-1 text-sm text-n-slate-12">
              <span>{{ $t('INTEGRATION_SETTINGS.DAFTARA.WEBHOOK_SECRET') }}</span>
              <Input v-model="connectionForm.webhookSecret" type="password" />
            </label>
          </div>
          <div class="mt-4 flex flex-wrap gap-2">
            <Button v-if="!connected" :disabled="!configured || !connectionForm.subdomain || !connectionForm.apiKey" :is-loading="isWorking" :label="$t('INTEGRATION_SETTINGS.DAFTARA.CONNECT')" @click="connect" />
            <Button v-if="connected" faded slate :is-loading="isWorking" :label="$t('INTEGRATION_SETTINGS.DAFTARA.SAVE_SETTINGS')" @click="saveSettings" />
          </div>
          <p v-if="status.webhook_url" class="mt-3 break-all text-sm text-n-slate-11">
            {{ $t('INTEGRATION_SETTINGS.DAFTARA.WEBHOOK_URL') }}: {{ status.webhook_url }}
          </p>
        </section>

        <section v-if="connected" class="rounded-lg outline outline-1 outline-n-container bg-n-card">
          <div class="flex flex-wrap gap-2 border-b border-n-weak p-3">
            <Button
              v-for="tab in tabs"
              :key="tab.key"
              size="sm"
              :variant="activeTab === tab.key ? 'solid' : 'faded'"
              color="slate"
              :label="tab.label"
              @click="switchTab(tab.key)"
            />
          </div>

          <div v-if="activeTab === 'webhooks'" class="p-4">
            <table class="min-w-full divide-y divide-n-weak text-sm">
              <thead>
                <tr class="text-left text-n-slate-11">
                  <th class="px-3 py-2">{{ $t('INTEGRATION_SETTINGS.DAFTARA.TABLE.EVENT') }}</th>
                  <th class="px-3 py-2">{{ $t('INTEGRATION_SETTINGS.DAFTARA.TABLE.ID') }}</th>
                  <th class="px-3 py-2">{{ $t('INTEGRATION_SETTINGS.DAFTARA.TABLE.RECEIVED') }}</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-n-weak text-n-slate-12">
                <tr v-for="event in recentWebhooks" :key="`${event.event}-${event.id}-${event.received_at}`">
                  <td class="px-3 py-3">{{ event.event }}</td>
                  <td class="px-3 py-3">{{ event.id || '-' }}</td>
                  <td class="px-3 py-3">{{ event.received_at }}</td>
                </tr>
                <tr v-if="!recentWebhooks.length">
                  <td colspan="3" class="px-3 py-8 text-center text-n-slate-11">{{ $t('INTEGRATION_SETTINGS.DAFTARA.EMPTY_WEBHOOKS') }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <template v-else>
            <div v-if="activeTab === 'creditUsages'" class="grid gap-3 border-b border-n-weak p-4 md:grid-cols-5">
              <Input v-model="creditUsageForm.client_id" :placeholder="$t('INTEGRATION_SETTINGS.DAFTARA.CLIENT_ID')" />
              <Input v-model="creditUsageForm.package_id" :placeholder="$t('INTEGRATION_SETTINGS.DAFTARA.PACKAGE_ID')" />
              <Input v-model="creditUsageForm.amount" type="number" :placeholder="$t('INTEGRATION_SETTINGS.DAFTARA.AMOUNT')" />
              <Input v-model="creditUsageForm.description" :placeholder="$t('INTEGRATION_SETTINGS.DAFTARA.DESCRIPTION_FIELD')" />
              <Button faded slate :disabled="!creditUsageForm.client_id" :is-loading="isWorking" :label="$t('INTEGRATION_SETTINGS.DAFTARA.ADD_CREDIT_USAGE')" @click="addCreditUsage" />
            </div>

            <div class="overflow-x-auto p-4">
              <table class="min-w-full divide-y divide-n-weak text-sm">
                <thead>
                  <tr class="text-left text-n-slate-11">
                    <th class="px-3 py-2">{{ $t('INTEGRATION_SETTINGS.DAFTARA.TABLE.NAME') }}</th>
                    <th class="px-3 py-2">{{ $t('INTEGRATION_SETTINGS.DAFTARA.TABLE.DETAIL') }}</th>
                    <th class="px-3 py-2">{{ $t('INTEGRATION_SETTINGS.DAFTARA.TABLE.STATUS') }}</th>
                    <th class="px-3 py-2">{{ $t('INTEGRATION_SETTINGS.DAFTARA.TABLE.ACTIONS') }}</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-n-weak text-n-slate-12">
                  <tr v-if="currentResource.loading">
                    <td colspan="4" class="px-3 py-8 text-center text-n-slate-11">{{ $t('INTEGRATION_SETTINGS.DAFTARA.LOADING') }}</td>
                  </tr>
                  <template v-else>
                    <tr v-for="(record, index) in currentItems" :key="unwrapRecord(record).id || `${activeTab}-${currentResource.page}-${index}`">
                      <td class="px-3 py-3">{{ rowTitle(record) }}</td>
                      <td class="px-3 py-3 text-n-slate-11">{{ rowDetail(record) }}</td>
                      <td class="px-3 py-3 text-n-slate-11">{{ rowStatus(record) }}</td>
                      <td class="px-3 py-3">
                        <div class="flex flex-wrap gap-2">
                          <Button v-if="activeTab === 'clients'" xs faded blue :is-loading="isWorking" :label="$t('INTEGRATION_SETTINGS.DAFTARA.IMPORT_CLIENT')" @click="importClient(record)" />
                          <Button v-if="canCreateConversation" xs faded slate :is-loading="isWorking" :label="$t(activeTab === 'subscriptions' ? 'INTEGRATION_SETTINGS.DAFTARA.REMIND_CLIENT' : 'INTEGRATION_SETTINGS.DAFTARA.CREATE_CONVERSATION')" @click="createConversation(record)" />
                        </div>
                      </td>
                    </tr>
                  </template>
                  <tr v-if="!currentResource.loading && !currentItems.length">
                    <td colspan="4" class="px-3 py-8 text-center text-n-slate-11">{{ $t('INTEGRATION_SETTINGS.DAFTARA.EMPTY') }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="flex items-center justify-between border-t border-n-weak p-4">
              <span class="text-sm text-n-slate-11">{{ $t('INTEGRATION_SETTINGS.DAFTARA.PAGE', { page: currentResource.page }) }}</span>
              <div class="flex gap-2">
                <Button xs faded slate :disabled="currentResource.page <= 1 || currentResource.loading" :label="$t('INTEGRATION_SETTINGS.DAFTARA.PREVIOUS')" @click="setPage(-1)" />
                <Button xs faded slate :disabled="!currentItems.length || currentResource.loading" :label="$t('INTEGRATION_SETTINGS.DAFTARA.NEXT')" @click="setPage(1)" />
              </div>
            </div>
          </template>
        </section>
      </div>
    </template>
  </SettingsLayout>
</template>
